/**
 * DB-backed ICU monitoring alerts for the central wall and dashboards.
 */
import apiClient from "@shared/services/apiClient";
import type { WallAlert } from "../utils/centralMonitoringWall";

export type MonitoringAlertDto = {
  alertId: string;
  studyId: string;
  patientId: string;
  alertType: string;
  severity: "info" | "warning" | "critical";
  message: string;
  timestamp: string;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  metadata?: Record<string, unknown>;
};

function unwrap<T>(response: { data?: { data?: T } | T }): T {
  const body = response?.data as { data?: T } | T | undefined;
  if (body && typeof body === "object" && "data" in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export function mapMonitoringAlertToWallAlert(
  alert: MonitoringAlertDto,
): WallAlert {
  return {
    alertId: alert.alertId,
    studyId: alert.studyId,
    severity: alert.severity,
    message: alert.message,
    raisedAtIso: alert.timestamp,
    audible: alert.severity !== "info",
    visual: true,
    acknowledged: Boolean(alert.acknowledged),
    acknowledgedAtIso: alert.acknowledgedAt ?? null,
    acknowledgedBy: alert.acknowledgedBy ?? null,
  };
}

export async function listMonitoringAlertsApi(params: {
  studyIds?: string[];
  hours?: number;
  unacknowledgedOnly?: boolean;
  limit?: number;
}): Promise<MonitoringAlertDto[]> {
  const response = await apiClient.get("/monitoring/alerts", {
    params: {
      studyIds: params.studyIds?.length ? params.studyIds.join(",") : undefined,
      hours: params.hours,
      unacknowledgedOnly: params.unacknowledgedOnly ? "true" : undefined,
      limit: params.limit,
    },
  });
  const data = unwrap<MonitoringAlertDto[]>(response);
  return Array.isArray(data) ? data : [];
}

export async function createMonitoringAlertApi(input: {
  studyId: string;
  patientId: string;
  recordingId: string;
  alertType: string;
  severity: "info" | "warning" | "critical";
  message: string;
  metadata?: Record<string, unknown>;
}): Promise<MonitoringAlertDto> {
  const response = await apiClient.post("/monitoring/alerts", input);
  return unwrap<MonitoringAlertDto>(response);
}

export async function acknowledgeMonitoringAlertApi(
  alertId: string,
): Promise<void> {
  await apiClient.patch(`/monitoring/alerts/${alertId}/acknowledge`);
}
